import { useLoadingStore } from '~/stores/loading';
import { useProp000Store } from '~/stores/property-manager/prop-000';
import { useMessageDialog } from '~/composables/useMessageDialog';
import { handleResponse } from '~/composables/handleResponse';
import { HTTP_STATUS_CODES } from '~/constants/http-status-codes';
import { type Router } from 'vue-router';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type RequestOptions = {
  query?: Record<string, any>;
  body?: Record<string, any>;
  headers?: Record<string, string>;
};

export type ResponseCallbacks<T> = {
  successCallback?: (response: T) => void;
  errorCallback?: (err: any) => void;
};

export type ApiResponse<T> = T;

let refreshPromise: Promise<string> | null = null;

const getHeaders = (customHeaders: Record<string, string> = {}, isAdmin: boolean = false): Record<string, string> => {
  const authStore = isAdmin ? useAdminUserStore() : usePropertyUserStore();
  const prop000Store = useProp000Store();

  const propertyId = prop000Store.GetPropertyId || localStorage.getItem('PROPERTY_ID');
  if (propertyId) customHeaders['PROPERTY-ID'] = propertyId;

  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...customHeaders
  };

  if (authStore.isAuthenticated) {
    headers = {
      Authorization: `Bearer ${authStore.accessToken}`,
      'username': encodeURIComponent(authStore?.userInfo?.name || ''),
      ...headers
    };
  }
  return headers;
};

export const apiRequest = async <T>(
  method: HttpMethod,
  endpoint: string,
  options: RequestOptions = {},
  responseCallbacks?: ResponseCallbacks<T>
): Promise<T> => {
  const loading = useLoadingStore();
  const {
    public: { API_URL, API_BASE_PATH }
  } = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const router = nuxtApp.$router as Router;

  const isAdmin = window.location.pathname.startsWith('/admin');
  const authStore = isAdmin ? useAdminUserStore() : usePropertyUserStore();

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_URL}/${API_BASE_PATH}/${isAdmin ? `admin/${endpoint}` : endpoint}`;

  let capturedHeaders: Headers | undefined;

  const doFetch = () => {
    const customHeaders = getHeaders(options.headers, isAdmin);
    if (options.body instanceof FormData) {
      delete customHeaders['Content-Type'];
    }

    return $fetch<T>(url, {
      method,
      mode: 'cors',
      query: options.query,
      headers: customHeaders,
      body: options.body,
      onResponse({ response }) {
        capturedHeaders = response.headers;
      }
    });
  };

  loading.startLoading();

  try {
    const executeWithRefresh = async (isRetry = false): Promise<T> => {
      try {
        const data = await doFetch();
        const isCSV = capturedHeaders?.get('Content-Type')?.includes('text/csv');
        const responseData = data instanceof Blob || isCSV ? { body: data } : { ...data };

        const handled = handleResponse({ ...responseData, headers: capturedHeaders }, responseCallbacks);
        if (!handled) throw new Error('Empty response');
        return handled;
      } catch (error: any) {
        const status = error.status || error.response?.status || 0;

        if (status !== HTTP_STATUS_CODES.UNAUTHORIZED || isRetry) {
          throw error;
        }

        try {
          if (!refreshPromise) {
            refreshPromise = authStore.refreshAccessToken().finally(() => {
              refreshPromise = null;
            });
          }
          await refreshPromise;
        } catch (refreshErr: any) {
          authStore.logout();
          throw refreshErr;
        }

        return await executeWithRefresh(true);
      }
    };

    return await executeWithRefresh();
  } catch (finalErr: any) {
    return handleApiError(finalErr, responseCallbacks, router);
  } finally {
    loading.stopLoading();
  }
};


const handleApiError = (error: any, responseCallbacks?: ResponseCallbacks<any>, router?: Router) => {
  const status = error.status || error.response?.status || 0;

  if (status === HTTP_STATUS_CODES.UNAUTHORIZED) {
    const isAdmin = window.location.pathname.startsWith('/admin');
    const authStore = isAdmin ? useAdminUserStore() : usePropertyUserStore();
    authStore.logout();
    return error;
  }

  if (status === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
    router?.push('/error');
  } else if ((error.name === 'FetchError' && error.message.includes('timeout')) || status === HTTP_STATUS_CODES.SERVICE_UNAVAILABLE) {
    useMessageDialog().show('リクエストに失敗しました、ネットワークタイムアウト。');
  } else if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
    useMessageDialog().show('ネットワーク接続エラー。');
  } else {
    let msg = error.response?._data?.message || 'リクエストに失敗しました';
    if(msg.includes('Failed to parse')){
      msg = 'リクエストに失敗しました' + 'エラーコード：' + status;
    }
    useMessageDialog().show(msg, ()=>{
      responseCallbacks?.errorCallback?.(error);
    });
  }

  return error;
};

export const useApi = {
  get: async <T>(endpoint: string, params: object = {}, headers: Record<string, string> = {}) =>
    apiRequest<T>('GET', endpoint, { query: params, headers }),
  post: async <T>(endpoint: string, body: object = {}, headers: Record<string, string> = {}, responseCallbacks?: ResponseCallbacks<T>) =>
    apiRequest<T>('POST', endpoint, { body, headers }, responseCallbacks),
  put: async <T>(endpoint: string, body: object = {}, headers: Record<string, string> = {}) => apiRequest<T>('PUT', endpoint, { body, headers }),
  delete: async <T>(endpoint: string, headers: Record<string, string> = {}) => apiRequest<T>('DELETE', endpoint, { headers })
};
